import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { HttpStatusCode } from "axios";
import { NavDropdown } from "react-bootstrap";
import { useTranslation } from "react-i18next";

import Logo from "./Logo";
import { LanguageSwitcher } from "./LanguageSwitcher";

import { Logout } from "../redux/authReducer";
import { setCourseStatus, ToggleHeaderOnSmallScreen } from "../redux/uiReducer";
import { loginService } from "../WebApiServices/login.service";
import { archiveActionService } from "../WebApiServices/archiveAction.service";

import {
  GetLocalStorageData,
  GetLoggedInUserId,
  HandleImageErr,
} from "../Helpers/Utilities";
import {
  ARCHIVE_ACTIONS,
  HEADER_MENU_DISPLAY_TYPE,
  PAGE_ROUTES,
  PLACEHOLDER_IMGS,
  COURSE,
  TRAINING_LOG_ACCESS,
} from "../Helpers/Enums";

const MainHeader = ({
  addHeaderPaddingTop: _addHeaderPaddingTop,
  HideMenuItems,
  navTheme = "light",
}) => {
  const { t: translation } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const auth = useSelector((state) => state.auth);
  const ui = useSelector((state) => state.ui);
  const location = useLocation();
  const [userProfileImage, setUserProfileImage] = useState(
    PLACEHOLDER_IMGS.PROFILE,
  );
  const [userName, setUserName] = useState("");
  const [showProfileBubble, setShowProfileBubble] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    if (auth.userData) {
      SetUserData(auth.userData);
    } else {
      let user = GetLocalStorageData("eUser");
      SetUserData(user);
    }

    window.scrollTo(0, 0);

    return () => {};
  }, [auth]);

  const labels = translation("header.labels", {
    returnObjects: true,
  });

  const HEADER_MENU_ITEMS = [
    {
      Id: 2,
      Name: labels.home,
      path: "Home",
      navigatePath: "/Home",
      displayType: HEADER_MENU_DISPLAY_TYPE.LoggedIn,
    },

    {
      Id: 3,
      Name: labels.courses,
      path: "Courses",
      navigatePath: "/Courses",
      displayType: HEADER_MENU_DISPLAY_TYPE.Both,
    },

    {
      Id: 6,
      Name: labels.manual,
      path: "TrainingLogBook",
      navigatePath: "/TrainingLogBook",
      displayType: HEADER_MENU_DISPLAY_TYPE.LoggedIn,
    },

    {
      Id: 7,
      Name: labels.log,
      path: "TrainingLog",
      navigatePath: "/TrainingLog",
      displayType: HEADER_MENU_DISPLAY_TYPE.LoggedIn,
    },

    {
      Id: 8,
      Name: labels.contact,
      path: "Contact",
      navigatePath: "/Contact",
      displayType: HEADER_MENU_DISPLAY_TYPE.Both,
    },
  ];

  const SetUserData = (updatedUserData) => {
    setUserProfileImage(
      updatedUserData?.userProfileImage || PLACEHOLDER_IMGS.PROFILE,
    );
    setUserName(updatedUserData?.userName || "");
  };

  const OnLoginLogoutButtonClicked = async () => {
    if (!auth.isLoggedIn) {
      navigate("/");
    } else {
      await loginService.Logout();
      LoggedOutArchiveAction(GetLoggedInUserId());
      localStorage.clear();
      dispatch(Logout());
      navigate("/");
    }
  };

  useEffect(() => {
    if (showProfileBubble) {
      let profileBubbleElement = document.getElementById("app");
      if (profileBubbleElement) {
        profileBubbleElement.addEventListener("click", OnDivClicked);
      }
    }

    return () => {
      let profileBubbleElement = document.getElementById("app");
      if (profileBubbleElement) {
        profileBubbleElement.removeEventListener("click", OnDivClicked);
      }
    };
  }, [showProfileBubble]);

  const OnDivClicked = (e) => {
    if (document.getElementById("navbarDropdown").contains(e.target)) {
      //console.log("You clicked on profile");
    } else {
      //console.log("You clicked on modal");
      if (!document.getElementById("profileBubble").contains(e.target)) {
        //if not clicked on button then hide list
        //console.log("You clicked outside modal");
        setShowProfileBubble(false);
      }
    }
  };

  const LoggedOutArchiveAction = async (userGUID) => {
    try {
      const { NUMBER, DATA } = ARCHIVE_ACTIONS.LOGGED_OUT;

      const response = await archiveActionService.AddArchive(
        NUMBER,
        DATA,
        userGUID,
        0,
        userGUID,
      );

      if (response.status !== HttpStatusCode.Ok) {
        throw response.error;
      }
    } catch (error) {
      //ShowToast(ARCHIVE_ACTIONS.ERROR, TOAST_TYPE.Error);
    }
  };

  /* <summary>
  date: 18-05-2023
  Name: GM
  description: Get class for Header nav items
  <summary>*/
  const GetNavLinkClass = (item) => {
    let className = "nav-link";

    if (["", "Home"].includes(item.path)) {
      if (location.pathname === "/" || location.pathname === "/Home") {
        className = `nav-link ${
          location.pathname.includes(item.path) ? "active" : ""
        }`;
      }
    } else {
      // Use exact match instead of includes to prevent partial matches
      className = `nav-link ${
        location.pathname === item.navigatePath ? "active" : ""
      }`;
    }

    return className;
  };

  /* <summary>
  date: 17-08-2023
  Name: VK
  description: Toggle to disappear for mobile and navigate
  <summary>*/
  const HideNavigateMobileMenu = (navigatePath) => {
    dispatch(ToggleHeaderOnSmallScreen());
    navigate(navigatePath);
  };

  const NavigateToCourses = (status = COURSE.STATUS.ASSIGNED_COURSES) => {
    dispatch(setCourseStatus(status));
    navigate("/Courses");
  };

  /* <summary>
  description: Check if user has Master rank
  <summary>*/
  const isMasterRank = () => {
    const user = auth.userData || GetLocalStorageData("eUser");

    // Check if user is Master or Chief Officer (C/O)
    return (
      user?.rankId === TRAINING_LOG_ACCESS.Master ||
      user?.rankId === TRAINING_LOG_ACCESS.CO
    );
  };

  /* <summary>
  date: 18-05-2023
  Name: GM
  description: Filter and return the Header Navigation items 
  <summary>*/
  const GetNavItems = () => {
    const { NotLoggedIn, LoggedIn } = HEADER_MENU_DISPLAY_TYPE;

    // First filter based on login status
    let filteredNavItems = HEADER_MENU_ITEMS.filter((t) =>
      GetLoggedInUserId()
        ? t.displayType !== NotLoggedIn
        : t.displayType !== LoggedIn,
    );

    // Check if user is Master rank
    const isMaster = isMasterRank();

    // Filter Training Log and Training Manual based on rank
    filteredNavItems = filteredNavItems.filter((t) => {
      // If user is Master rank
      if (isMaster) {
        // Show Training Log, hide Training Manual
        if (t.path === "TrainingLogBook") {
          return false;
        }
        if (t.path === "TrainingLog") {
          return true;
        }
      } else {
        // For non-Master users
        // Show Training Manual, hide Training Log
        if (t.path === "TrainingLog") {
          return false;
        }
        if (t.path === "TrainingLogBook") {
          return true;
        }
      }

      return true; // Show all other menu items
    });

    const COURSE_NAV = [
      {
        label: labels.assigned,
        value: COURSE.STATUS.ASSIGNED_COURSES,
      },
      {
        label: labels.ongoing,
        value: COURSE.STATUS.ONGOING_COURSES,
      },
      {
        label: labels.completed,
        value: COURSE.STATUS.COMPLETED_COURSES,
      },
    ];

    const navThemeClass =
      !scrolled && navTheme === "dark"
        ? "nav-item custom-nav-dark"
        : "nav-item";

    const navItems = filteredNavItems.map((t) => {
      if (t.path !== "Courses") {
        return (
          <li key={t.Id} className={navThemeClass}>
            <button
              className={GetNavLinkClass(t)}
              onClick={() => HideNavigateMobileMenu(t.navigatePath)}
              data-bs-toggle="dropdown"
              aria-expanded="false"
            >
              {t.Name?.toUpperCase()}{" "}
            </button>
          </li>
        );
      }

      return (
        <li key={t.Id} className={navThemeClass}>
          <NavDropdown
            title={labels.courses.toUpperCase()}
            id="basic-nav-dropdown"
            className="custom-dropdown-menu"
          >
            {COURSE_NAV.map((n) => (
              <NavDropdown.Item
                key={n.value}
                className={
                  ui.courseStatus === n.value ? GetNavLinkClass(t) : ""
                }
                onClick={() => NavigateToCourses(n.value)}
                style={{
                  fontSize: "18px",
                  textAlign: "center",
                  padding: "8px 12px",
                }}
              >
                {n.label}
              </NavDropdown.Item>
            ))}
          </NavDropdown>
        </li>
      );
    });

    return [...navItems];
  };

  useEffect(() => {
    const handleScroll = () => {
      const isScrolled = window.scrollY > 0;
      if (isScrolled !== scrolled) {
        setScrolled(isScrolled);
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [scrolled]);

  return (
    <section
      className={"main-header sticky-top"}
      style={{
        backgroundColor: scrolled ? "#fff" : "rgba(242, 249, 255, 0.16)",
      }}
    >
      <div className="container-fluid">
        <div className="row">
          {/* LOGO */}
          <div className="col-6 col-sm-5 col-md-3 col-lg-3">
            <div className="logo-box" style={{ maxWidth: "240px" }}>
              <Link className="navbar-brand" to="/Home">
                <Logo />
              </Link>
            </div>
          </div>

          <div className="col-sm-12 col-md-12 col-lg-9">
            {!HideMenuItems && (
              <nav
                className={`navbar navbar-expand-lg navbar-light ${
                  auth.isLoggedIn ? "userlogin" : ""
                }`}
              >
                <button
                  className="navbar-toggler nav-button"
                  type="button"
                  data-bs-toggle="collapse"
                  data-bs-target="#navbarNav"
                  aria-controls="navbarNav"
                  aria-expanded="false"
                  aria-label="Toggle navigation"
                  onClick={() => dispatch(ToggleHeaderOnSmallScreen())}
                >
                  <span className="navbar-toggler-icon" />
                </button>

                <div
                  key="menu"
                  className={`justify-content-end collapse navbar-collapse ${
                    ui.showHeaderOnSmallScreen ? "show" : ""
                  }`}
                  id="navbarNav"
                >
                  <ul className="navbar-nav menu-ul">{GetNavItems()}</ul>

                  <LanguageSwitcher />

                  {auth.isLoggedIn ? (
                    <ul className="navbar-nav">
                      <li className="nav-item dropdown admin-dropdown">
                        <button
                          className="nav-link dropdown-toggle d-flex align-items-center"
                          id="navbarDropdown"
                          data-bs-toggle="dropdown"
                          aria-expanded="false"
                          onClick={() =>
                            setShowProfileBubble((prevState) => !prevState)
                          }
                        >
                          <img
                            src={userProfileImage}
                            className="header-profile-img"
                            alt="profile"
                            width="40px"
                            height="40px"
                            title={userName}
                            onError={(e) =>
                              HandleImageErr(e, PLACEHOLDER_IMGS.PROFILE_ERROR)
                            }
                          />
                        </button>

                        {/* PROFILE DROPDOWN */}
                        <ul
                          className={`dropdown-menu custom-dropdown-menu client-main-dropdown ${
                            showProfileBubble ? "show" : ""
                          }`}
                          id="profileBubble"
                          aria-labelledby="navbarDropdown"
                        >
                          {/* PROFILE */}
                          <li>
                            <Link
                              className="dropdown-item"
                              to={PAGE_ROUTES.PROFILE_PAGE}
                              title={userName}
                              style={{
                                maxWidth: "200px",
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                              }}
                            >
                              <span className="profile-className">
                                <img
                                  src="assets/images/menuProfile.png"
                                  width="35"
                                  height="35"
                                  alt="User Profile Menu"
                                />
                              </span>
                              {userName}
                            </Link>
                          </li>

                          {/* LOGOUT */}
                          <li>
                            <a
                              className="dropdown-item"
                              onClick={OnLoginLogoutButtonClicked}
                            >
                              <span className="logout-class">
                                <img
                                  src="assets/images/menuLogout.png"
                                  width="30"
                                  height="30"
                                  alt="Logout"
                                />
                              </span>{" "}
                              {translation("labels.logout")}
                            </a>
                          </li>
                        </ul>
                      </li>
                    </ul>
                  ) : (
                    <div className="header-btn">
                      <Link
                        id="login-header-btn"
                        className="btn blue-btn fs-3"
                        to={"/"}
                      >
                        {translation("labels.login")}
                      </Link>
                    </div>
                  )}
                </div>
              </nav>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default MainHeader;
